// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import translate from "@insite/client-framework/Translate";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { styledBoxStyle } from "@keller/App/Components/Common/StyledBox";
import { reviewSectionAddressesStyles } from "@keller/Overrides/Widgets/CheckoutShipping/SharedAddresses.style";
import { ShippingAddressInfo } from "@keller/XDL/types";
import { color, mergeWithCss } from "@keller/XDL/Utilities";
import React from "react";
import { css } from "styled-components";

interface OwnProps {
    pickUpShippingAddress: ShippingAddressInfo;
    extendedStyles?: PickUpShippingAddressInfoDisplay;
}
export interface PickUpShippingAddressInfoDisplay {
    container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    heading?: TypographyPresentationProps;
    editLink?: LinkPresentationProps;
    addressGridItem?: GridItemProps;
    address?: AddressInfoDisplayStyles;
}
export const pickUpShippingAddressInfoDisplay: PickUpShippingAddressInfoDisplay = {
    container: mergeWithCss(reviewSectionAddressesStyles.container, {
        css: css`
            ${GridOffset} {
                @media print {
                    margin: 0;
                    display: flex;
                    flex-direction: column;
                    flex-wrap: nowrap;
                    flex: 1;
                }
            }
            @media print {
                display: flex;
                flex-direction: column;
                max-width: 500px;
                height: 100%;
            }
        `,
    }),
    headingGridItem: mergeWithCss(reviewSectionAddressesStyles.headingGridItem, {
        css: css`
            @media print {
                background-color: ${color("common.white")};
                border: 1px solid ${color("common.border")};
                border-bottom: 0;
                padding: 8px;
                margin: 0;
                display: flex;
                flex: 0;
            }
        `,
    }),
    heading: mergeWithCss(reviewSectionAddressesStyles.heading, {
        css: css`
            @media print {
                font-size: 12px;
                line-height: 18px;
            }
        `,
    }),
    addressGridItem: {
        width: 12,
        css: css`
            &&& {
                flex-direction: column;
                @media print {
                    ${styledBoxStyle}
                    padding: 8px;
                    display: flex;
                    flex-direction: column;
                }
            }
        `,
    },
    address: {
        companyNameText: reviewSectionAddressesStyles.address?.companyNameText,
    },
};
const PickUpShippingAddressInfoDisplay = ({ pickUpShippingAddress, extendedStyles }: OwnProps) => {
    const styles = mergeToNew(pickUpShippingAddressInfoDisplay, extendedStyles);
    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.headingGridItem}>
                <Typography {...styles.heading}>{translate("Shipping Address")}</Typography>
            </GridItem>
            <GridItem {...styles.addressGridItem}>
                <AddressInfoDisplay {...pickUpShippingAddress} extendedStyles={styles.address} />
            </GridItem>
        </GridContainer>
    );
};
export default PickUpShippingAddressInfoDisplay;
